import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Link } from 'react-router-dom';

/**
 * Presentational fields shared by the standalone signup page
 * (/auth/register) and the camp-page guest checkout when the user opts in
 * to creating an account. Logic lives in `useAccountCreation`.
 *
 * Props:
 *   values              { first_name, last_name, email, phone, password, confirm_password }
 *   onChange(field, v)
 *   errors              { [field]: 'error message' }  (optional; shows red border + inline msg)
 *   verifyMethod        'phone' | 'email'
 *   setVerifyMethod     (method) => void
 *   agreed              boolean   (T&C acceptance)
 *   setAgreed           (bool) => void
 *   smsConsent          boolean
 *   setSmsConsent       (bool) => void
 *   referralSlot        React node (optional referral-code input)
 *   variant             'light' | 'dark'   styling mode (default 'light')
 *   showTerms           default true — the camp page hides this since the cart already has consent wording
 */
export default function AccountCreationFields({
  values,
  onChange,
  errors = {},
  verifyMethod,
  setVerifyMethod,
  agreed,
  setAgreed,
  smsConsent,
  setSmsConsent,
  referralSlot = null,
  variant = 'light',
  showTerms = true,
}) {
  const dark = variant === 'dark';
  const labelCls = dark ? 'text-white' : '';
  const inputCls = dark ? 'bg-white/10 border-white/20 text-white' : '';
  const helpCls = dark ? 'text-white/60' : 'text-muted-foreground';
  const errCls = 'border-red-500 focus-visible:ring-red-500';
  const errMsg = (field) =>
    errors[field] ? (
      <p className="text-xs text-red-500 mt-1" data-testid={`register-error-${field}`}>{errors[field]}</p>
    ) : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2" data-register-field="first_name">
          <Label className={labelCls}>First Name *</Label>
          <Input
            value={values.first_name}
            onChange={(e) => onChange('first_name', e.target.value)}
            placeholder="First name"
            required
            className={`${inputCls} ${errors.first_name ? errCls : ''}`}
            data-testid="register-first-name"
          />
          {errMsg('first_name')}
        </div>
        <div className="space-y-2" data-register-field="last_name">
          <Label className={labelCls}>Last Name *</Label>
          <Input
            value={values.last_name}
            onChange={(e) => onChange('last_name', e.target.value)}
            placeholder="Last name"
            required
            className={`${inputCls} ${errors.last_name ? errCls : ''}`}
            data-testid="register-last-name"
          />
          {errMsg('last_name')}
        </div>
      </div>

      <div className="space-y-2" data-register-field="email">
        <Label className={labelCls}>Email <span className="text-red-500">*</span></Label>
        <Input
          type="email"
          value={values.email}
          onChange={(e) => onChange('email', e.target.value)}
          placeholder="your@email.com"
          required
          className={`${inputCls} ${errors.email ? errCls : ''}`}
          data-testid="register-email"
        />
        {errMsg('email')}
      </div>

      <div className="space-y-2" data-register-field="phone">
        <Label className={labelCls}>Mobile Phone *</Label>
        <Input
          type="tel"
          value={values.phone}
          onChange={(e) => onChange('phone', e.target.value)}
          placeholder="(555) 123-4567"
          required
          className={`${inputCls} ${errors.phone ? errCls : ''}`}
          data-testid="register-phone"
        />
        {errors.phone ? errMsg('phone') : (
          <p className={`text-xs ${helpCls} mt-1`}>
            Used to verify your account, send class reminders, and enable phone sign-in.
          </p>
        )}
      </div>

      {referralSlot}

      <div className="space-y-2" data-register-field="password">
        <Label className={labelCls}>Password *</Label>
        <Input
          type="password"
          value={values.password}
          onChange={(e) => onChange('password', e.target.value)}
          placeholder="Min 8 characters"
          required
          className={`${inputCls} ${errors.password ? errCls : ''}`}
          data-testid="register-password"
        />
        {errMsg('password')}
      </div>

      <div className="space-y-2" data-register-field="confirm_password">
        <Label className={labelCls}>Confirm Password *</Label>
        <Input
          type="password"
          value={values.confirm_password}
          onChange={(e) => onChange('confirm_password', e.target.value)}
          placeholder="Confirm password"
          required
          className={`${inputCls} ${errors.confirm_password ? errCls : ''}`}
          data-testid="register-confirm-password"
        />
        {errMsg('confirm_password')}
      </div>

      {showTerms && (
        <div className="space-y-1" data-register-field="agreed">
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className={`rounded mt-1 ${errors.agreed ? 'ring-2 ring-red-500' : ''}`}
              data-testid="register-agreed-checkbox"
            />
            <p className={`text-sm ${helpCls}`}>
              I agree to the{' '}
              <Link to="/terms" className="text-primary underline">Terms of Service</Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-primary underline">Privacy Policy</Link>
            </p>
          </div>
          {errMsg('agreed')}
        </div>
      )}

      {values.phone && setSmsConsent && (
        <div className={`flex items-start gap-2 p-3 rounded-lg border ${dark ? 'bg-white/5 border-white/10' : 'bg-gray-50'}`}>
          <input
            type="checkbox"
            checked={smsConsent}
            onChange={(e) => setSmsConsent(e.target.checked)}
            className="rounded mt-1"
          />
          <p className={`text-xs ${helpCls} leading-relaxed`}>
            I consent to receive text messages (e.g., class reminders, enrollment updates) from Ready Set STEM at the phone number provided. Consent is not a condition of purchase. Msg &amp; data rates may apply. Reply STOP to unsubscribe.{' '}
            <Link to="/privacy" className="text-primary underline">Privacy Policy</Link> &amp;{' '}
            <Link to="/terms" className="text-primary underline">Terms</Link>.
          </p>
        </div>
      )}

      {/* Verification method toggle */}
      <div className="space-y-2 pt-1">
        <Label className={`text-xs uppercase tracking-wide ${helpCls}`}>
          Verify your account via
        </Label>
        <div
          className={`grid grid-cols-2 gap-2 p-1 rounded-lg ${dark ? 'bg-white/10' : 'bg-gray-100'}`}
          role="radiogroup"
          aria-label="Verification method"
        >
          <button
            type="button"
            role="radio"
            aria-checked={verifyMethod === 'phone'}
            onClick={() => setVerifyMethod('phone')}
            className={`py-2 text-sm font-medium rounded-md transition-all ${
              verifyMethod === 'phone'
                ? (dark ? 'bg-white text-rsc-navy shadow' : 'bg-white shadow text-rsc-navy')
                : (dark ? 'text-white/70' : 'text-gray-500')
            }`}
            data-testid="verify-method-phone-btn"
          >
            Text message (SMS)
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={verifyMethod === 'email'}
            onClick={() => setVerifyMethod('email')}
            className={`py-2 text-sm font-medium rounded-md transition-all ${
              verifyMethod === 'email'
                ? (dark ? 'bg-white text-rsc-navy shadow' : 'bg-white shadow text-rsc-navy')
                : (dark ? 'text-white/70' : 'text-gray-500')
            }`}
            data-testid="verify-method-email-btn"
          >
            Email
          </button>
        </div>
      </div>
    </div>
  );
}
